import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App, signOut } from './App';
import { LoginPage } from './pages/LoginPage';
import { AccessDenied } from './pages/AccessDenied';
import { api, ApiError } from './lib/api';
import { initSupabase } from './lib/supabase';
import { C, ink, PAGE_BG } from './lib/theme';
import { ToastProvider } from './components/Toast';
import type { Me } from './lib/types';
import './styles.css';

function Boot() {
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  // `denied` = autenticou no Supabase, mas não está na allowlist do ambiente.
  const [denied, setDenied] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let alive = true;
    initSupabase()
      .then(async ({ client }) => {
        if (!alive) return;
        const { data } = await client.auth.getSession();
        setEmail(data.session?.user.email ?? null);
        setReady(true);
        // Mantém a UI em sincronia com logout/refresh de token em outra aba.
        client.auth.onAuthStateChange((_evt, session) => {
          setEmail(session?.user.email ?? null);
        });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setBootError(err instanceof Error ? err.message : 'Falha ao inicializar.');
        setReady(true);
      });
    return () => { alive = false; };
  }, []);

  /**
   * Com sessão em mãos, pergunta ao backend quem sou eu. É esta chamada que
   * aplica a allowlist: 403 `no_access` significa autenticado porém não
   * autorizado — a distinção que separa "entrou" de "pode usar".
   */
  useEffect(() => {
    if (!ready || bootError || !email) { setMe(null); setDenied(false); return; }
    let alive = true;
    setChecking(true);
    api.me()
      .then((profile) => { if (alive) { setMe(profile); setDenied(false); } })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && (err.code === 'no_access' || err.status === 403)) {
          setMe(null); setDenied(true);
        } else {
          setBootError(err instanceof ApiError ? err.message : 'Falha ao verificar seu acesso.');
        }
      })
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
  }, [ready, bootError, email]);

  if (!ready) {
    return (
      <div style={centered}>
        <div style={{ fontSize: 12, color: ink(0.4), letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          carregando…
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div style={centered}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 750, marginBottom: 8 }}>Não foi possível iniciar</div>
          <div style={{ fontSize: 13, color: ink(0.5), lineHeight: 1.55 }}>{bootError}</div>
          <div style={{ fontSize: 12, color: ink(0.32), marginTop: 14 }}>
            Verifique se o backend está rodando em <strong>/config</strong>.
          </div>
        </div>
      </div>
    );
  }

  if (!email) return <LoginPage onSignedIn={() => { /* onAuthStateChange atualiza o email */ }} />;

  if (denied) return <AccessDenied email={email} onSignOut={() => void signOut()} />;

  if (checking || !me) {
    return (
      <div style={centered}>
        <div style={{ fontSize: 12, color: ink(0.4), letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          verificando acesso…
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <App
        me={me} onSignOut={() => void signOut()}
        onMeUpdated={(patch) => setMe((m) => (m ? { ...m, ...patch } : m))}
      />
    </BrowserRouter>
  );
}

const centered: React.CSSProperties = {
  minHeight: '100vh', background: PAGE_BG, color: C.fg,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ToastProvider>
      <Boot />
    </ToastProvider>
  </StrictMode>,
);
